import { describeVariant, tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Loading,
  Modal,
  Mono,
  Screen,
} from '../../components/ui.js';
import { ItemSearch } from '../../components/item-search.js';
import { useNotice } from '../../components/notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import './receiving-document.css';

/**
 * Returns to vendor — "RTV" (2026-10-01, decision 181).
 *
 * The negative of a receipt. Written while the goods are boxed up, printed to
 * go in the box, and **posted when the vendor's credit comes** — only then do
 * the units leave stock. An open one is paperwork: it can be changed, and
 * deleted with nothing on the shelf moving. Lives on the Purchase orders tab,
 * beside receiving, rather than as an eighth Stock tab (§2.2-2).
 */

type Reason = 'overshipped' | 'damaged' | 'warranty' | 'wrong_item' | 'other';

export const RETURN_REASONS: readonly { value: Reason; label: string }[] = [
  { value: 'overshipped', label: 'Over-shipped' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'other', label: 'Other' },
];

const reasonLabel = (reason: string): string =>
  RETURN_REASONS.find((entry) => entry.value === reason)?.label ?? reason;

const STATUS: Record<string, { label: string; tone: 'info' | 'success' | 'neutral' }> = {
  open: { label: 'Open', tone: 'info' },
  posted: { label: 'Posted', tone: 'success' },
  deleted: { label: 'Deleted', tone: 'neutral' },
};

type Detail = Awaited<ReturnType<typeof api.purchasing.vendorReturn.query>>;

// --- the list, on the Purchase orders tab -----------------------------------

export function VendorReturnsCard({ onOpen }: { onOpen: (id: string) => void }) {
  const [showDeleted, setShowDeleted] = useState(false);
  const returns = useQuery({
    queryKey: ['purchasing', 'vendorReturns', showDeleted],
    queryFn: () => api.purchasing.vendorReturns.query({ includeDeleted: showDeleted }),
    staleTime: 0,
  });
  const rows = returns.data ?? [];

  return (
    <Card
      title="Returns to vendor"
      padded={false}
      actions={
        <label className="toggle">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(event) => setShowDeleted(event.target.checked)}
          />
          <span>Show deleted</span>
        </label>
      }
    >
      {returns.isPending ? (
        <Loading label="Loading returns" />
      ) : rows.length === 0 ? (
        <p className="text-secondary receiving__empty">
          Nothing going back. Press Return to vendor to write one.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Return</th>
                <th>Vendor</th>
                <th>Authorization</th>
                <th className="num">Units</th>
                <th className="num">At cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => onOpen(row.id)}>
                      <Mono>{row.number}</Mono>
                    </button>
                  </td>
                  <td>{row.vendorName}</td>
                  <td>{row.authorization ?? <span className="text-secondary">—</span>}</td>
                  <td className="num">{row.units}</td>
                  <td className="num money">{money(row.costCents)}</td>
                  <td>
                    <Badge tone={STATUS[row.status]?.tone ?? 'neutral'}>
                      {STATUS[row.status]?.label ?? row.status}
                    </Badge>
                    {row.status === 'posted' && row.creditCents !== null ? (
                      <span className="text-secondary"> · credit {money(row.creditCents)}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// --- writing, posting and deleting one --------------------------------------

interface DraftLine {
  readonly variantId: string;
  readonly description: string;
  readonly sku: string;
  readonly onHand: number | null;
  quantity: number;
  reason: Reason;
  fromStock: boolean;
}

export function VendorReturnScreen({
  vendorReturnId,
  canPost,
  onClose,
}: {
  /** Null to write a new one. */
  vendorReturnId: string | null;
  canPost: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [id, setId] = useState<string | null>(vendorReturnId);
  const [vendorId, setVendorId] = useState('');
  const [authorization, setAuthorization] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [dirty, setDirty] = useState(false);
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);
  const printer = useVendorReturnDocument();

  const vendors = useQuery({
    queryKey: ['purchasing', 'vendors', 'active'],
    queryFn: () => api.purchasing.vendors.query(),
  });

  const detail = useQuery({
    queryKey: ['purchasing', 'vendorReturn', id],
    queryFn: () => api.purchasing.vendorReturn.query({ id: id! }),
    enabled: id !== null,
    staleTime: 0,
  });

  // Load the saved form once; after that, what is typed here is the truth
  // until it is saved.
  const loaded = useRef<string | null>(null);
  useEffect(() => {
    const data = detail.data;
    if (data === undefined || loaded.current === data.vendorReturn.id) return;
    loaded.current = data.vendorReturn.id;
    setVendorId(data.vendorReturn.vendorId);
    setAuthorization(data.vendorReturn.authorization ?? '');
    setNotes(data.vendorReturn.notes ?? '');
    setLines(
      data.lines.map((line) => ({
        variantId: line.variantId,
        description: line.description,
        sku: line.sku,
        onHand: line.onHand,
        quantity: line.quantity,
        reason: line.reason as Reason,
        fromStock: line.fromStock,
      })),
    );
    setDirty(false);
  }, [detail.data]);

  const status = detail.data?.vendorReturn.status ?? 'open';
  const editable = status === 'open';
  const number = detail.data?.vendorReturn.number ?? null;

  const save = useMutation({
    mutationFn: async () => {
      const input = {
        vendorId,
        purchaseOrderId: detail.data?.vendorReturn.purchaseOrderId ?? null,
        authorization: authorization.trim() === '' ? null : authorization,
        notes: notes.trim() === '' ? null : notes,
        lines: lines.map((line) => ({
          variantId: line.variantId,
          quantity: line.quantity,
          reason: line.reason,
          fromStock: line.fromStock,
        })),
      };
      return id === null
        ? api.purchasing.saveVendorReturn.mutate(input)
        : api.purchasing.saveVendorReturn.mutate({ id, ...input });
    },
    onSuccess: (saved) => {
      recordBreadcrumb('purchasing', `saved return ${saved.number}`);
      const units = lines.reduce((sum, line) => sum + line.quantity, 0);
      notify({ title: `Saved — ${saved.number}, ${units} ${units === 1 ? 'unit' : 'units'}` });
      loaded.current = null;
      setId(saved.id);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['purchasing', 'vendorReturns'] });
      void queryClient.invalidateQueries({ queryKey: ['purchasing', 'vendorReturn', saved.id] });
    },
  });

  const addOne = (found: { variantId: string; description: string; sku: string }) => {
    setDirty(true);
    setLines((current) => {
      const existing = current.find((line) => line.variantId === found.variantId);
      if (existing !== undefined) {
        return current.map((line) =>
          line.variantId === found.variantId ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [
        ...current,
        { ...found, onHand: null, quantity: 1, reason: 'overshipped', fromStock: true },
      ];
    });
    scanRef.current?.focus();
  };

  const change = (variantId: string, patch: Partial<DraftLine>) => {
    setDirty(true);
    setLines((current) =>
      current.map((line) => (line.variantId === variantId ? { ...line, ...patch } : line)),
    );
  };

  const saveReason =
    vendorId === ''
      ? 'Choose the vendor'
      : lines.length === 0
        ? 'Add what is going back'
        : lines.some((line) => !Number.isInteger(line.quantity) || line.quantity < 1)
          ? 'Every line needs a quantity of one or more'
          : undefined;

  const units = lines.reduce((sum, line) => sum + line.quantity, 0);

  return (
    <Screen
      title={number === null ? 'Return to vendor' : `Return ${number}`}
      subtitle={
        status === 'posted'
          ? 'Posted — the units have left stock.'
          : status === 'deleted'
            ? 'Deleted — nothing left stock.'
            : 'Nothing leaves stock until it is posted.'
      }
      width="wide"
      destructiveAction={
        <>
          <Button variant="ghost" onClick={onClose}>
            {dirty ? 'Leave without saving' : 'Back to purchase orders'}
          </Button>
          {editable && id !== null ? (
            <Button variant="danger" onClick={() => setDeleting(true)}>
              Delete return
            </Button>
          ) : null}
        </>
      }
      secondaryActions={
        id !== null && detail.data !== undefined ? (
          <Button
            variant="secondary"
            disabledReason={dirty ? 'Save first, so the paper matches' : undefined}
            onClick={() => printer.print(detail.data!)}
          >
            Print return
          </Button>
        ) : undefined
      }
      primaryAction={
        editable ? (
          dirty || id === null ? (
            <Button
              variant="primary"
              busy={save.isPending}
              disabledReason={saveReason}
              onClick={() => save.mutate()}
            >
              Save return · {units} unit{units === 1 ? '' : 's'}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabledReason={canPost ? undefined : 'Posting takes stock out; a manager posts it'}
              onClick={() => setPosting(true)}
            >
              Post — credit received
            </Button>
          )
        ) : undefined
      }
    >
      {printer.frame}
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}

      {id !== null && detail.isPending ? (
        <Card>
          <Loading label="Loading the return" />
        </Card>
      ) : (
        <>
          <Card>
            <div className="rtv__head">
              <Field label="Vendor">
                {(props) => (
                  <SearchSelect
                    {...props}
                    placeholder="Choose the vendor — type to search"
                    value={vendorId}
                    disabled={!editable}
                    options={(vendors.data ?? []).map((vendor) => ({
                      value: vendor.id,
                      label: vendor.name,
                    }))}
                    onChange={(chosen) => {
                      if (chosen === vendorId) return;
                      setVendorId(chosen);
                      setDirty(true);
                    }}
                  />
                )}
              </Field>
              <Field label="Their return authorization">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    placeholder="RA 55120"
                    value={authorization}
                    disabled={!editable}
                    onChange={(event) => {
                      setAuthorization(event.target.value);
                      setDirty(true);
                    }}
                  />
                )}
              </Field>
              <Field label="Notes">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    value={notes}
                    disabled={!editable}
                    onChange={(event) => {
                      setNotes(event.target.value);
                      setDirty(true);
                    }}
                  />
                )}
              </Field>
            </div>
            {status === 'posted' && detail.data !== undefined ? (
              <p className="text-secondary">
                Credit{' '}
                {detail.data.vendorReturn.creditCents === null
                  ? 'not recorded'
                  : money(detail.data.vendorReturn.creditCents)}
                {detail.data.vendorReturn.creditReference === null
                  ? ''
                  : ` · their credit memo ${detail.data.vendorReturn.creditReference}`}
              </p>
            ) : null}
          </Card>

          {editable ? (
            <ItemSearch
              inputRef={scanRef}
              label="Add an item"
              placeholder="Scan or type what is going back…"
              nothingFound={(term) => `Nothing in the catalog matches ${term}.`}
              onPick={(item) =>
                addOne({
                  variantId: item.variantId,
                  sku: item.sku,
                  description: describeVariant({
                    productName: item.name,
                    colorName: item.color,
                    sizeLabel: item.size,
                    altSize: item.altSize,
                  }),
                })
              }
            />
          ) : null}

          <Card padded={false}>
            {lines.length === 0 ? (
              <p className="text-secondary receiving__empty">
                Nothing on it yet. Scan each item going back, or type its SKU or name.
              </p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="num">On hand</th>
                      <th className="num">Sending</th>
                      <th>Reason</th>
                      <th>From our stock</th>
                      {editable ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.variantId}>
                        <td>
                          {line.description}
                          <div className="text-secondary">
                            <Mono>{line.sku}</Mono>
                          </div>
                        </td>
                        <td className="num">{line.onHand ?? '—'}</td>
                        <td className="num">
                          <input
                            className="qty-input num"
                            inputMode="numeric"
                            aria-label={`Sending, ${line.description}`}
                            value={String(line.quantity)}
                            disabled={!editable}
                            onChange={(event) =>
                              change(line.variantId, {
                                quantity: Number(
                                  event.target.value.replace(/\D/g, '').slice(0, 4) || 0,
                                ),
                              })
                            }
                          />
                        </td>
                        <td>
                          <select
                            className="select"
                            aria-label={`Reason, ${line.description}`}
                            value={line.reason}
                            disabled={!editable}
                            onChange={(event) => {
                              const reason = event.target.value as Reason;
                              // A warranty item is most often a customer's, never ours.
                              change(line.variantId, {
                                reason,
                                ...(reason === 'warranty' ? { fromStock: false } : {}),
                              });
                            }}
                          >
                            {RETURN_REASONS.map((entry) => (
                              <option key={entry.value} value={entry.value}>
                                {entry.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <label className="toggle">
                            <input
                              type="checkbox"
                              aria-label={`From our stock, ${line.description}`}
                              checked={line.fromStock}
                              disabled={!editable}
                              onChange={(event) =>
                                change(line.variantId, { fromStock: event.target.checked })
                              }
                            />
                            <span>{line.fromStock ? 'Yes' : 'No — not ours'}</span>
                          </label>
                        </td>
                        {editable ? (
                          <td>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setDirty(true);
                                setLines((current) =>
                                  current.filter((entry) => entry.variantId !== line.variantId),
                                );
                              }}
                            >
                              Remove
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {posting && id !== null && detail.data !== undefined ? (
        <PostDialog
          detail={detail.data}
          onClose={() => setPosting(false)}
          onPosted={() => {
            setPosting(false);
            loaded.current = null;
            void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
            void queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      ) : null}
      {deleting && id !== null && number !== null ? (
        <DeleteDialog
          id={id}
          number={number}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
            onClose();
          }}
        />
      ) : null}
    </Screen>
  );
}

function PostDialog({
  detail,
  onClose,
  onPosted,
}: {
  detail: Detail;
  onClose: () => void;
  onPosted: () => void;
}) {
  const notify = useNotice();
  const [credit, setCredit] = useState('');
  const [reference, setReference] = useState('');
  const taking = detail.lines.filter((line) => line.fromStock);
  const units = taking.reduce((sum, line) => sum + line.quantity, 0);
  const creditCents = credit.trim() === '' ? null : tryParseMoney(credit);

  const post = useMutation({
    mutationFn: () =>
      api.purchasing.postVendorReturn.mutate({
        id: detail.vendorReturn.id,
        creditCents,
        creditReference: reference.trim() === '' ? null : reference,
      }),
    onSuccess: (result) => {
      recordBreadcrumb('purchasing', `posted return ${result.number}`);
      notify({
        title:
          units === 0
            ? `${result.number} posted`
            : `${result.number} posted — ${units} ${units === 1 ? 'unit' : 'units'} out of stock`,
        ...(creditCents === null ? {} : { detail: `Credit received: ${money(creditCents)}.` }),
      });
      onPosted();
    },
  });

  return (
    <Modal
      open
      title={`Post ${detail.vendorReturn.number}`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Not yet
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={post.isPending}
          disabledReason={
            credit.trim() !== '' && creditCents === null
              ? 'Type the credit as an amount'
              : undefined
          }
          onClick={() => post.mutate()}
        >
          Post · take {units} unit{units === 1 ? '' : 's'} out of stock
        </Button>
      }
    >
      <p>
        Post it when the vendor&rsquo;s credit has come.{' '}
        {units === 0
          ? 'Nothing on it is from our stock, so stock does not change.'
          : `${units} unit${units === 1 ? '' : 's'} leave stock now, at their average cost.`}
      </p>
      <Field label="Credit received">
        {(props) => (
          <input
            {...props}
            className="input money"
            inputMode="decimal"
            placeholder={(detail.totals.costCents / 100).toFixed(2)}
            value={credit}
            onChange={(event) => setCredit(event.target.value)}
          />
        )}
      </Field>
      <Field label="Their credit memo number">
        {(props) => (
          <input
            {...props}
            className="input"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        )}
      </Field>
      {post.isError ? <Callout tone="danger">{errorMessage(post.error)}</Callout> : null}
    </Modal>
  );
}

function DeleteDialog({
  id,
  number,
  onClose,
  onDeleted,
}: {
  id: string;
  number: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const notify = useNotice();
  const remove = useMutation({
    mutationFn: () => api.purchasing.deleteVendorReturn.mutate({ id }),
    onSuccess: () => {
      notify({ title: `${number} deleted` });
      recordBreadcrumb('purchasing', `deleted return ${number}`);
      onDeleted();
    },
  });
  return (
    <Modal
      open
      title={`Delete ${number}?`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Keep it
        </Button>
      }
      primaryAction={
        <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
          Delete return
        </Button>
      }
    >
      <p>
        Nothing has left stock, so nothing changes on the shelf. The return is kept, marked deleted.
      </p>
      {remove.isError ? <Callout tone="danger">{errorMessage(remove.error)}</Callout> : null}
    </Modal>
  );
}

// --- the paper that goes in the box -----------------------------------------

const PAGE_RULE = '@page { size: letter; margin: 0.6in; }';

function useVendorReturnDocument() {
  const [printing, setPrinting] = useState<Detail | null>(null);
  const print = useCallback((detail: Detail) => {
    const style = window.document.createElement('style');
    style.textContent = PAGE_RULE;
    window.document.head.appendChild(style);
    try {
      flushSync(() => setPrinting(detail));
      window.print();
    } finally {
      setPrinting(null);
      style.remove();
    }
  }, []);
  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <VendorReturnDocument detail={printing} />
          </div>,
          window.document.body,
        );
  return { print, frame };
}

function VendorReturnDocument({ detail }: { detail: Detail }) {
  const header = detail.vendorReturn;
  return (
    <article className="rodoc">
      <header className="rodoc__head">
        <div>
          {detail.store.map((line, index) => (
            <div key={index} className={index === 0 ? 'rodoc__store' : undefined}>
              {line}
            </div>
          ))}
        </div>
        <div className="rodoc__title">
          <h1>Return to vendor</h1>
          <div className="rodoc__number">{header.number}</div>
        </div>
      </header>
      <dl className="rodoc__meta">
        <div>
          <dt>Vendor</dt>
          <dd>
            {header.vendorName}
            {[
              header.vendorAddressLine1,
              header.vendorAddressLine2,
              [header.vendorCity, header.vendorRegion].filter(Boolean).join(', ') +
                (header.vendorPostalCode === null ? '' : ` ${header.vendorPostalCode}`),
              header.vendorPhone,
              header.vendorEmail,
            ]
              .filter((line) => line !== null && line.trim() !== '')
              .map((line, index) => (
                <div key={index}>{line}</div>
              ))}
          </dd>
        </div>
        {header.vendorAccountNumber === null ? null : (
          <div>
            <dt>Account</dt>
            <dd>{header.vendorAccountNumber}</dd>
          </div>
        )}
        <div>
          <dt>Return authorization</dt>
          <dd>{header.authorization ?? '—'}</dd>
        </div>
        {header.purchaseOrderNumber === null ? null : (
          <div>
            <dt>Their order</dt>
            <dd>{header.purchaseOrderNumber}</dd>
          </div>
        )}
        <div>
          <dt>Written</dt>
          <dd>{new Date(header.createdAt).toLocaleDateString()}</dd>
        </div>
      </dl>
      <table className="rodoc__lines">
        <thead>
          <tr>
            <th>Item</th>
            <th>SKU</th>
            <th>Mfr #</th>
            <th>Reason</th>
            <th className="num">Qty</th>
            <th className="num">Cost</th>
            <th className="num">Extension</th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((line) => (
            <tr key={line.variantId}>
              <td>{line.description}</td>
              <td>{line.sku}</td>
              <td>{line.vendorPartNumber ?? ''}</td>
              <td>{reasonLabel(line.reason)}</td>
              <td className="num">{line.quantity}</td>
              <td className="num">{money(line.unitCostCents)}</td>
              <td className="num">{money(line.quantity * line.unitCostCents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>Total</td>
            <td className="num">{detail.totals.units}</td>
            <td />
            <td className="num">{money(detail.totals.costCents)}</td>
          </tr>
        </tfoot>
      </table>
      {header.notes === null ? null : <p className="rodoc__notes">{header.notes}</p>}
      <div className="rodoc__sign">
        <div>
          <span className="rodoc__rule" />
          Packed by
        </div>
        <div>
          <span className="rodoc__rule" />
          Date shipped
        </div>
      </div>
    </article>
  );
}
